<template>
  <div class="camera-capture fixed inset-0 z-30 flex flex-col bg-black text-white">
    <!-- Top bar -->
    <div class="camera-top z-20 px-4 pt-4 pb-8">
      <div class="flex items-center justify-between">
        <button class="close-button" aria-label="Close" @click="$emit('close')">
          <Icon type="xmark" fa-style="far" class="text-lg" />
        </button>
        <div class="text-sm font-medium opacity-80">{{ progressLabel }}</div>
        <div class="w-[36px]" />
      </div>

      <div class="text-center mt-3">
        <div class="text-xl font-semibold">{{ label }}</div>
        <div v-if="hint" class="text-sm opacity-70 mt-1">{{ hint }}</div>
      </div>

      <div v-if="progressPercent !== null" class="progress-track mt-4">
        <div class="progress-fill" :style="{ width: progressPercent + '%' }" />
      </div>
    </div>

    <!-- Viewfinder / preview area -->
    <div class="flex-grow relative overflow-hidden">
      <video
        v-show="phase === 'camera' && cameraSupported"
        ref="videoElement"
        class="absolute inset-0 w-full h-full object-cover"
        autoplay
        playsinline
        muted
      />

      <div v-if="phase === 'starting'" class="absolute inset-0 flex flex-col items-center justify-center gap-3">
        <Icon type="circle-notch" class="text-3xl" spinner />
        <div class="text-sm opacity-70">Starting camera...</div>
      </div>

      <!-- Fallback (no in-browser camera) -->
      <div
        v-if="phase === 'camera' && !cameraSupported"
        class="absolute inset-0 flex flex-col items-center justify-center gap-4 p-8 text-center"
      >
        <div class="fallback-icon">
          <Icon type="camera" fa-style="far" class="text-3xl" />
        </div>
        <div class="text-lg font-medium">{{ label }}</div>
        <p class="text-sm opacity-70 max-w-[280px]">We'll open your device's camera so you can take this photo.</p>
      </div>

      <!-- Captured still preview -->
      <img
        v-if="phase === 'preview' && capturedUrl"
        :src="capturedUrl"
        alt="Captured photo"
        class="absolute inset-0 w-full h-full object-cover"
      />
    </div>

    <!-- Bottom controls -->
    <div class="camera-bottom z-20 px-6 pt-6 pb-8">
      <div v-if="phase === 'camera'" class="flex items-center justify-center">
        <button v-if="cameraSupported" class="shutter-button" aria-label="Capture photo" @click="capturePhoto">
          <span class="shutter-inner" />
        </button>
        <Button v-else label="Open Camera" icon="camera" size="lg" class="accent-button" @click="openNativeCamera" />
      </div>

      <div v-else-if="phase === 'preview'" class="flex gap-3">
        <Button label="Retake" icon="rotate-left" outlined severity="secondary" block class="retake-button" @click="retake" />
        <Button label="Continue" icon="arrow-right" icon-pos="right" block class="accent-button" @click="confirmPhoto" />
      </div>
    </div>

    <!-- Hidden native camera input (fallback) -->
    <input ref="fallbackInput" type="file" accept="image/*" capture="environment" class="hidden" @change="onFallbackFile" />
    <canvas ref="captureCanvas" class="hidden" />
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue'

import Button from '@/components/button/Button.vue'
import Icon from '@/components/icon/Icon.vue'

defineProps({
  label: {
    type: String,
    required: true
  },
  hint: {
    type: String,
    default: null
  },
  progressLabel: {
    type: String,
    default: ''
  },
  progressPercent: {
    type: Number,
    default: null
  }
})

const emit = defineEmits(['close', 'captured'])

const videoElement = ref(null)
const captureCanvas = ref(null)
const fallbackInput = ref(null)

// starting | camera | preview
const phase = ref('starting')
const cameraSupported = ref(true)
const capturedBlob = ref(null)
const capturedUrl = ref(null)

let mediaStream = null

async function startCamera() {
  if (!navigator.mediaDevices?.getUserMedia) {
    cameraSupported.value = false
    phase.value = 'camera'
    return
  }

  try {
    mediaStream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
      audio: false
    })
    if (videoElement.value) {
      videoElement.value.srcObject = mediaStream
    }
    cameraSupported.value = true
  } catch (error) {
    console.warn(`In-browser camera unavailable, falling back to native camera: ${error.message}`)
    cameraSupported.value = false
  }
  phase.value = 'camera'
}

function stopCamera() {
  if (mediaStream) {
    mediaStream.getTracks().forEach(track => track.stop())
    mediaStream = null
  }
}

function capturePhoto() {
  const video = videoElement.value
  if (!video || !video.videoWidth) return

  const canvas = captureCanvas.value
  canvas.width = video.videoWidth
  canvas.height = video.videoHeight
  canvas.getContext('2d').drawImage(video, 0, 0)

  canvas.toBlob(
    blob => {
      if (blob) {
        setCapturedImage(blob)
      }
    },
    'image/jpeg',
    0.85
  )
}

function openNativeCamera() {
  fallbackInput.value.click()
}

function onFallbackFile(event) {
  const file = event.target.files?.[0]
  event.target.value = ''
  if (file) {
    setCapturedImage(file)
  }
}

function setCapturedImage(blob) {
  revokeCapturedUrl()
  capturedBlob.value = blob
  capturedUrl.value = URL.createObjectURL(blob)
  phase.value = 'preview'
}

function revokeCapturedUrl() {
  if (capturedUrl.value) {
    URL.revokeObjectURL(capturedUrl.value)
    capturedUrl.value = null
  }
  capturedBlob.value = null
}

function retake() {
  revokeCapturedUrl()
  phase.value = 'camera'
  if (!cameraSupported.value) {
    openNativeCamera()
  }
}

function confirmPhoto() {
  if (!capturedBlob.value) return
  const blob = capturedBlob.value
  // Hand ownership of the blob to the parent; don't revoke it here
  capturedBlob.value = null
  if (capturedUrl.value) {
    URL.revokeObjectURL(capturedUrl.value)
    capturedUrl.value = null
  }
  emit('captured', blob)
}

// Called by the parent after handling 'captured', to line up the next photo
function reset() {
  revokeCapturedUrl()
  phase.value = 'camera'
  if (!cameraSupported.value) {
    // Small delay so the heading updates before the native camera opens
    setTimeout(() => openNativeCamera(), 300)
  }
}

defineExpose({ reset })

onMounted(() => {
  startCamera()
})

onBeforeUnmount(() => {
  stopCamera()
  revokeCapturedUrl()
})
</script>

<style scoped>
.camera-capture {
  background-color: #0b1526;
}

.camera-top {
  background: linear-gradient(to bottom, rgba(11, 21, 38, 0.95), rgba(11, 21, 38, 0.75) 70%, transparent);
}

.camera-bottom {
  background: linear-gradient(to top, rgba(11, 21, 38, 0.95), rgba(11, 21, 38, 0.75) 70%, transparent);
}

.close-button {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.12);
}

.progress-track {
  height: 4px;
  border-radius: 100px;
  background-color: rgba(255, 255, 255, 0.15);
  overflow: hidden;
}
.progress-fill {
  height: 100%;
  border-radius: 100px;
  background-color: var(--manufacturer-color);
  transition: width 0.4s ease;
}

.shutter-button {
  width: 74px;
  height: 74px;
  border-radius: 50%;
  border: 4px solid white;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
}
.shutter-inner {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: white;
  transition: transform 0.1s ease;
}
.shutter-button:active .shutter-inner {
  transform: scale(0.85);
  background-color: var(--manufacturer-color);
}

.fallback-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.1);
}

.accent-button {
  background-color: var(--manufacturer-color) !important;
}

.retake-button {
  color: white !important;
  border-color: rgba(255, 255, 255, 0.4) !important;
}
.retake-button:enabled:hover {
  background-color: rgba(255, 255, 255, 0.1) !important;
}
</style>
